import { withMockQueryClient } from '../../stories/mockQueryClient';
import { iimReviewKeys } from '../../hooks/queryKeys';
import TechnicianScorecardDrawer from './index';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
// Derived at module load so the "N days ago" label stays stable regardless of when Storybook runs.
const FIRST_OCCURRENCE_DATE = new Date(Date.now() - 93 * MS_PER_DAY).toISOString();
const ASSET_NAME = 'Antifreeze 1372';
const MOCK_SCORECARD = {
    technicianId: 42,
    name: 'Jane Smith',
    status: 'complete',
    currentRating: 'needs_coaching',
    currentScore: 32,
    scoreTrend: 'declining',
    issueTypeBreakdown: [
        { issueType: 'unanswered_questions', count: 26 },
        { issueType: 'missing_deficiency_notes_photos', count: 18 },
        { issueType: 'recurring_deficiencies', count: 9 }
    ],
    kpis: {
        numberOfInspections: { value: 64, previousValue: 59 },
        resolutionRate: { value: 93.0, previousValue: 81.0 },
        avgResubmissions: { value: 0.8, previousValue: 2.0 },
        deficienciesPerInspection: { value: 12.4, previousValue: 5.3 },
        avgCompletionTimeVsBookedMinutes: { value: -24.0, previousValue: -22.8 },
        callbacksReturnVisits: { value: 0, previousValue: 0 }
    },
    weeklyIssuesTrend: [
        { weekStart: '2026-08-10', detected: 2, dismissed: 1 },
        { weekStart: '2026-08-17', detected: 4, dismissed: 2 },
        { weekStart: '2026-08-24', detected: 3, dismissed: 3 },
        { weekStart: '2026-08-31', detected: 5, dismissed: 1 }
    ],
    performanceFlags: [
        {
            type: 'documentation_gaps',
            unansweredCount: 3,
            missingNotesCount: 2,
            occurredAt: '2026-08-28T00:00:00Z',
            inspectionId: 1,
            isCurrentInspection: true,
            unansweredSectionsCount: 1,
            assetName: ASSET_NAME
        }
    ]
};
// The stories render the drawer without an inspectionId, so the component builds the key without one.
const SCORECARD_KEY = iimReviewKeys.technicianScorecard(MOCK_SCORECARD.technicianId);
const seed = (scenario) => withMockQueryClient([{ queryKey: SCORECARD_KEY, scenario }]);
const withScorecard = (overrides) => seed({ status: 'success', data: Object.assign(Object.assign({}, MOCK_SCORECARD), overrides) });
const NULL_KPI = { value: null, previousValue: null };
const meta = {
    title: 'Features/IIMReview/TechnicianScorecardDrawer',
    component: TechnicianScorecardDrawer,
    parameters: {
        layout: 'fullscreen'
    },
    decorators: [seed({ status: 'success', data: MOCK_SCORECARD })],
    args: {
        technicianId: 42,
        isOpen: true,
        onClose: () => undefined
    }
};
export default meta;
export const WithData = {};
export const Loading = {
    decorators: [seed({ status: 'loading' })]
};
export const Error = {
    parameters: {
        docs: {
            description: {
                story: 'The component has no error UI yet, so the drawer body renders empty. Documents a known gap.'
            }
        }
    },
    decorators: [seed({ status: 'error' })]
};
export const Pending = {
    decorators: [
        withScorecard({
            status: 'pending',
            currentRating: null,
            currentScore: null,
            scoreTrend: null,
            issueTypeBreakdown: []
        })
    ]
};
export const Closed = {
    args: {
        isOpen: false
    }
};
export const NullKpis = {
    decorators: [
        withScorecard({
            kpis: {
                numberOfInspections: NULL_KPI,
                resolutionRate: NULL_KPI,
                avgResubmissions: NULL_KPI,
                deficienciesPerInspection: NULL_KPI,
                avgCompletionTimeVsBookedMinutes: NULL_KPI,
                callbacksReturnVisits: NULL_KPI
            }
        })
    ]
};
export const NoRating = {
    decorators: [withScorecard({ currentRating: null, currentScore: null, scoreTrend: null })]
};
export const NoPerformanceFlags = {
    decorators: [withScorecard({ performanceFlags: [] })]
};
const RECURRING_FLAG = {
    type: 'recurring_deficiency_pattern',
    title: 'Test?',
    occurrencesCount: 9,
    daysSpan: 93,
    unresolvedCount: 9,
    occurredAt: '2026-08-30T00:00:00Z',
    firstOccurrenceDate: FIRST_OCCURRENCE_DATE,
    assetName: ASSET_NAME
};
const DOC_GAPS_FLAG = {
    type: 'documentation_gaps',
    unansweredCount: 6,
    missingNotesCount: 1,
    occurredAt: '2026-08-28T00:00:00Z',
    inspectionId: 1,
    isCurrentInspection: true,
    unansweredSectionsCount: 1,
    assetName: ASSET_NAME
};
const ASSET_COUNT_FLAG = {
    type: 'asset_count_change',
    priorCount: 0,
    currentCount: 4,
    sinceDate: '05-28-2026',
    occurredAt: '2026-08-20T00:00:00Z'
};
export const AllPerformanceFlags = {
    decorators: [withScorecard({ performanceFlags: [RECURRING_FLAG, DOC_GAPS_FLAG, ASSET_COUNT_FLAG] })]
};
export const PartiallyResolvedRecurring = {
    decorators: [withScorecard({ performanceFlags: [Object.assign(Object.assign({}, RECURRING_FLAG), { unresolvedCount: 4 })] })]
};
export const DocGapsMultipleAssets = {
    decorators: [
        withScorecard({
            performanceFlags: [Object.assign(Object.assign({}, DOC_GAPS_FLAG), { assetName: null, unansweredSectionsCount: null })]
        })
    ]
};
export const EmptyIssueTypeBreakdown = {
    decorators: [withScorecard({ issueTypeBreakdown: [] })]
};
export const NoWeeklyTrendData = {
    decorators: [withScorecard({ weeklyIssuesTrend: [] })]
};
export const QuietWeeklyTrend = {
    decorators: [
        withScorecard({
            weeklyIssuesTrend: [
                { weekStart: '2026-08-10', detected: 0, dismissed: 0 },
                { weekStart: '2026-08-17', detected: 0, dismissed: 0 }
            ]
        })
    ]
};
export const IssueBreakdownTopFive = {
    decorators: [
        withScorecard({
            issueTypeBreakdown: [
                { issueType: 'unanswered_questions', count: 120 },
                { issueType: 'missing_deficiency_notes_photos', count: 18 },
                { issueType: 'recurring_deficiencies', count: 9 },
                { issueType: 'missing_signature', count: 6 },
                { issueType: 'missing_time_entry', count: 4 },
                { issueType: 'duplicate_photo_detected', count: 2 }
            ]
        })
    ]
};
export const NoChangeDeltas = {
    decorators: [
        withScorecard({
            kpis: {
                numberOfInspections: { value: 60, previousValue: 60 },
                resolutionRate: { value: 90, previousValue: 90 },
                avgResubmissions: { value: 1, previousValue: 1 },
                deficienciesPerInspection: { value: 5, previousValue: 5 },
                avgCompletionTimeVsBookedMinutes: { value: 0, previousValue: 0 },
                callbacksReturnVisits: { value: 0, previousValue: 0 }
            }
        })
    ]
};
export const WorseningDeltas = {
    decorators: [
        withScorecard({
            kpis: {
                numberOfInspections: { value: 40, previousValue: 59 },
                resolutionRate: { value: 70, previousValue: 90 },
                avgResubmissions: { value: 3, previousValue: 1 },
                deficienciesPerInspection: { value: 14, previousValue: 5 },
                avgCompletionTimeVsBookedMinutes: { value: 18, previousValue: -5 },
                callbacksReturnVisits: { value: 4, previousValue: 1 }
            }
        })
    ]
};
export const NoPreviousValues = {
    decorators: [
        withScorecard({
            kpis: {
                numberOfInspections: { value: 64, previousValue: null },
                resolutionRate: { value: 93, previousValue: null },
                avgResubmissions: { value: 0.8, previousValue: null },
                deficienciesPerInspection: { value: 12.4, previousValue: null },
                avgCompletionTimeVsBookedMinutes: { value: -24, previousValue: null },
                callbacksReturnVisits: { value: 0, previousValue: null }
            }
        })
    ]
};
